import { Anchor, Box, Button, Center, Group, Loader, LoadingOverlay, Stack, Table, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconLink, IconRefresh } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { queryClient } from "src/queryClient";

export function useArchiveCert(certId: string, onArchive?: () => void) {
    const blockers = useSuspenseQuery(gql.blockers(certId));

    const { mutateAsync } = useGraphqlMutation(gql.archive);

    const Modal = () => {
        const blockers = useSuspenseQuery(gql.blockers(certId));

        return (
            <Stack>
                <Text>Certificate category can not be archived until all dependent items have been removed</Text>
                <Box pos="relative">
                    <LoadingOverlay visible={blockers.isFetching} />
                    <Table striped>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Type</Table.Th>
                                <Table.Th>Description</Table.Th>
                                <Table.Th w={1}>
                                    <Button
                                        variant="default"
                                        rightSection={<IconRefresh />}
                                        onClick={() => {
                                            queryClient
                                                .invalidateQueries({ queryKey: ["ipaws", "cert", "blockers", certId] })
                                                .catch(notify.catch);
                                        }}
                                    >
                                        Refresh
                                    </Button>
                                </Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {blockers.data.ipawsCertificate.blockers.ipawsDraftAlerts.map((it) => (
                                <Table.Tr key={it.id}>
                                    <Table.Td>
                                        <Text fz="sm">Draft Alert</Text>
                                    </Table.Td>
                                    <Table.Td>Headline: {it.capMessage.infos.at(0)?.headline ?? "No subject"}</Table.Td>
                                    <Table.Td>
                                        <Anchor c="var(--mantine-color-text)" target="_blank" href="/ipaws/drafts" aria-label="Drafts">
                                            <IconLink size={16} />
                                        </Anchor>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                            {blockers.data.ipawsCertificate.blockers.ipawsDraftAlerts.length == 0 && (
                                <Table.Tr>
                                    <Table.Td colSpan={3}>
                                        <Center fs="italic">No remaining drafts</Center>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                        </Table.Tbody>
                    </Table>
                    <Group w="100%" justify="flex-end" py="xs">
                        <Button
                            disabled={blockers.data.ipawsCertificate.blockers.isBlocked}
                            onClick={() =>
                                void mutateAsync({ id: certId })
                                    .then(onArchive)
                                    .then(modals.closeAll)
                                    .then(() => notify.show.success("Archived certificate"))
                                    .catch(notify.catch)
                            }
                        >
                            Archive
                        </Button>
                    </Group>
                </Box>
            </Stack>
        );
    };

    async function archiveCert() {
        if (blockers.data.ipawsCertificate.blockers.isBlocked)
            modals.open({
                title: "Archive Certificate",
                children: (
                    <Suspense fallback={<Loader />}>
                        <Modal />
                    </Suspense>
                ),
                onClose: modals.closeAll,
                size: "lg",
            });
        else {
            await notify.confirm("Are you sure you want to archive this certificate?");
            mutateAsync({ id: certId }).then(onArchive).then(modals.closeAll).catch(notify.catch);
        }
    }

    return {
        archiveCert,
    };
}

const gql = {
    blockers: (certId: string) =>
        queryOptions({
            queryKey: ["ipaws", "cert", "blockers", certId],
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query CertBlockers($id: UUID!) {
                            ipawsCertificate(id: $id) {
                                blockers {
                                    isBlocked
                                    ipawsDraftAlerts {
                                        id
                                        capMessage {
                                            id
                                            infos {
                                                headline
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    `),
                    { id: certId },
                ),
        }),
    archive: graphql(`
        mutation ArchiveCert($id: UUID!) {
            archiveIpawsCertificate(id: $id) {
                id
            }
        }
    `),
};
